import { faSquareArrowDownRight } from "@fortawesome/pro-solid-svg-icons/faSquareArrowDownRight";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Box, CardContent, Grid, Stack, Typography, useTheme } from '@mui/material';
import { APP_DEFAULT_EXPORT_PAGE_SIZE, APP_PAGE_SIZE_MEDIUM } from 'app/appConstants';
import { AxiosResponse } from 'axios';
import { MprcCard, MprcChipChoice, MprcClickable, MprcContainer, MprcIconButton, MprcJoinComponents, MprcListItemTitle, MprcRequestErrorReport, MprcRequestIndicator, MprcSpinnerIndicator, MprcText } from 'components';
import { MprcFilterCount, MprcFilterLine } from 'components/filter';
import { MprcCardFilterBox } from 'components/filter/MprcCardFilterBox';
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { MprcSearchTextField } from 'components/MprcSearchTextField';
import { MprcSwitch } from "components/MprcSwitch";
import { GetGeneralUsersPayload, TrainingIndexOrderBy, TrainingIndexUserDto, TrainingIndexUserDtoPaginatedList } from 'generated/CoreApi_v1';
import HtmlUtils from "lib/HtmlUtils";
import { useModalUserProfile } from 'providers/UserProfileProvider';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashPagination } from "views/dashboard/components/DashPagination";
import { TrainingIndexResult } from 'views/dashboard/TrainingIndex/components/TrainingIndexResult';
import { TrainingIndexProps } from 'views/dashboard/TrainingIndex/TrainingIndexRoot';

////////////////////////////////////////////////////////////////////////////
interface TrainingIndexUsersFilter {
    orderBy: TrainingIndexOrderBy;
    searchText: string;
    pageNumber: number;
}
interface IProps extends TrainingIndexProps {
    onDrillDown?: (user: TrainingIndexUserDto) => void;
    onDrillDownTeam?: (userId: string) => void;
    getApi: (data: GetGeneralUsersPayload, other?: any) => Promise<AxiosResponse<TrainingIndexUserDtoPaginatedList, any>>;
    exportApi?: (data: GetGeneralUsersPayload, other?: any) => Promise<AxiosResponse<any>>;
    showOrderByFilter?: boolean;
    showAggregateFilter?: boolean;
    onExportData?: () => void;
}

////////////////////////////////////////////////////////////////////////////

export const TrainingIndexUsers: React.FC<IProps> = ({ currentMonth, getApi, onDrillDown, onDrillDownTeam, exportApi, showOrderByFilter = true, showAggregateFilter = false, ...props }) => {

    const { t } = useTranslation("dashboard");
    const { showUser } = useModalUserProfile();
    const theme = useTheme();

    const [aggregate, setAggregate] = useState<boolean>(false);
    const [viewData, setViewData] = useState<TrainingIndexUserDtoPaginatedList>();
    const [viewError, setViewError] = useState<any>();
    const [loading, setLoading] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [filter, setFilter] = useState<TrainingIndexUsersFilter>({
        orderBy: TrainingIndexOrderBy.Best,
        searchText: "",
        pageNumber: 1
    });

    const effectiveAggregate = showAggregateFilter && aggregate;

    ///
    const handleFilter = (filter: Partial<TrainingIndexUsersFilter>) => {
        setFilter(state => ({ ...state, ...filter }));
    };

    ///
    const toggleAggregate = () => {
        setAggregate(!aggregate);
        handleFilter({ searchText: "" });
    };

    ///
    const handleExportExcel = async () => {
        if (!exportApi) return;
        setDownloading(true);
        return HtmlUtils.downloadBlobFromPromise(exportApi({
            yearMonth: currentMonth,
            orderBy: filter.orderBy,
            searchText: null,
            pageNumber: 1,
            pageSize: APP_DEFAULT_EXPORT_PAGE_SIZE
        }, effectiveAggregate)
            .catch(MprcRequestSnackbarErrorReport)
            .finally(() => setDownloading(false)));
    };

    useEffect(() => {
        setLoading(true);
        getApi({
            yearMonth: currentMonth,
            orderBy: filter.orderBy,
            searchText: filter.searchText,
            pageNumber: filter.pageNumber,
            pageSize: APP_PAGE_SIZE_MEDIUM
        }, effectiveAggregate
        ).then(response => setViewData(response.data))
            .catch(e => setViewError(e))
            .finally(() => setLoading(false));
    }, [filter, aggregate, currentMonth, getApi]);

    return viewError
        ? <MprcRequestErrorReport error={viewError} />
        : <MprcCard>
            <MprcRequestIndicator type="downloading" visible={downloading} />
            <MprcContainer margin="dense">
                <MprcCardFilterBox>
                    <Box px={theme.spacing(2)}>
                        <MprcFilterLine>
                            <Typography variant="h2">{t("TrainingIndex.IndexByUserTitle")}</Typography>
                            {showOrderByFilter &&
                                <Stack direction="row" justifyContent="center" flexGrow={1}>
                                    <MprcChipChoice
                                        label={t("TrainingIndex.BestResultsOption")}
                                        disabled={loading}
                                        uppercase
                                        selected={filter.orderBy == TrainingIndexOrderBy.Best}
                                        onClick={() => handleFilter({ orderBy: TrainingIndexOrderBy.Best })}
                                    />
                                    <MprcChipChoice
                                        label={t("TrainingIndex.WorstResultsOption")}
                                        disabled={loading}
                                        uppercase
                                        selected={filter.orderBy == TrainingIndexOrderBy.Worst}
                                        onClick={() => handleFilter({ orderBy: TrainingIndexOrderBy.Worst })}
                                    />
                                </Stack>
                            }
                            {showAggregateFilter &&
                                <Stack direction="row" width={150} justifyContent="flex-end">
                                    <MprcSwitch
                                        checked={aggregate}
                                        onChange={toggleAggregate}
                                        label={t("TrainingIndex.BySubTeamLabel")}
                                    />
                                </Stack>
                            }
                        </MprcFilterLine>
                        <MprcSearchTextField
                            confirmMode="onEnter"
                            initialValue={filter.searchText}
                            placeholder={t("TrainingIndex.SearchTextPlaceholder")}
                            onConfirm={(value) => handleFilter({ searchText: value })}
                        />
                    </Box>
                </MprcCardFilterBox>
            </MprcContainer>
            <CardContent>
                {loading
                    ? <MprcSpinnerIndicator />
                    : viewData && <>
                        <MprcContainer>
                            <Stack spacing={1}>
                                <>
                                    <MprcContainer>
                                        <MprcFilterCount
                                            loading={loading}
                                            isEmpty={viewData.totalRows == 0}
                                            countText={t("TrainingIndex.UserRecordsFound", { count: viewData.totalRows })}
                                            filter={{
                                                active: filter.searchText.length > 0,
                                                text: t("TrainingIndex.UserRecordsFiltered", { count: viewData.totalRows }),
                                                onResetFilter: () => handleFilter({ pageNumber: 1, searchText: "" })
                                            }}
                                        />
                                    </MprcContainer>
                                    {viewData.items.map(item =>
                                        <Grid container key={item.userId} alignItems="center" spacing={1}>
                                            <Grid>
                                                <MprcClickable onClick={() => showUser(item.userId)}>
                                                    <Avatar src={item.thumbnailUrl} sx={{ width: 48, height: 48 }} />
                                                </MprcClickable>
                                            </Grid>
                                            <Grid size="grow">
                                                <Stack>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <MprcText color="warning" title={item.name}>
                                                            <MprcListItemTitle onClick={onDrillDown ? () => onDrillDown(item) : undefined} color={item.inGracePeriod ? "muted" : undefined}>
                                                                {item.displayName}
                                                            </MprcListItemTitle>
                                                        </MprcText>
                                                        {item.hasTeam && effectiveAggregate && onDrillDownTeam &&
                                                            <MprcIconButton onClick={() => onDrillDownTeam(item.userId)}>
                                                                <FontAwesomeIcon icon={faSquareArrowDownRight} />
                                                            </MprcIconButton>
                                                        }
                                                    </Stack>
                                                    <Typography variant={item.inGracePeriod ? "bodyNotice" : "caption"}>
                                                        <MprcJoinComponents
                                                            separator="&bull;"
                                                            components={[
                                                                item.jobPosition,
                                                                item.inGracePeriod ? <Typography variant="captionHeader">{t("TrainingIndex.InGracePeriodText")}</Typography> : null
                                                            ]}
                                                        />
                                                    </Typography>
                                                </Stack>
                                            </Grid>
                                            <Grid>
                                                <TrainingIndexResult
                                                    value={item.trainingIndex}
                                                    onClick={
                                                        effectiveAggregate
                                                            ? onDrillDownTeam ? () => onDrillDownTeam(item.userId) : undefined
                                                            : onDrillDown ? () => onDrillDown(item) : undefined
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    )}
                                </>

                            </Stack>
                        </MprcContainer>
                        <DashPagination
                            pageNumber={viewData.pageNumber}
                            totalPages={viewData.totalPages}
                            onSetPageNumber={(page) => handleFilter({ pageNumber: page })}
                            onExportData={handleExportExcel}
                        />
                    </>
                }
            </CardContent>
        </MprcCard>
        ;
};
